import { ERequestType } from "../../Enum/ERequestType"
import { EEnableStatusType } from "../../Enum/EEnableStatusType"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { AdminReseller } from "../../Objects/AdminReseller/AdminReseller"
import { ResellerCommissionPlan } from "../../Objects/AdminReseller/ResellerCommissionPlan"
import { GetRequest, PostRequest, PutRequest } from "../../services/ApiClientService"
import type { ResellerClient } from "../Reseller/useGetResellerClients"
import type { ResellerCommissionEntry } from "../Reseller/useGetResellerCommissions"

export interface AdminResellerDetail {
  reseller: AdminReseller
  clients: ResellerClient[]
  commissionEntries: ResellerCommissionEntry[]
}

export interface SaveResellerCommissionPlanRequest {
  name: string
  commissionPercentage: number
  durationType: number
  durationInMonths?: number
  isActive: boolean
  isDefault: boolean
}

export const useAdminReseller = () => {
  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const handleGetAllResellers = async (onSuccess: (data: AdminReseller[]) => void) => {
    enableLoad()

    try {
      const response = await GetRequest<AdminReseller[]>(API_ROUTES.ADMIN_RESELLER.GET_ALL, ERequestType.Admin)

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess((response.data ?? []).map((item) => new AdminReseller(item)))
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro ao carregar os revendedores."
      setError("Revendedores", message)
    } finally {
      disableLoad()
    }
  }

  const handleGetResellerDetail = async (resellerId: number, onSuccess: (data: AdminResellerDetail) => void) => {
    enableLoad()

    try {
      const response = await GetRequest<AdminResellerDetail>(
        API_ROUTES.ADMIN_RESELLER.GET_BY_ID(resellerId),
        ERequestType.Admin,
      )

      if (!response.success || !response.data) {
        throw new Error(response.message)
      }

      onSuccess({ ...response.data, reseller: new AdminReseller(response.data.reseller) })
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro ao carregar o detalhe do revendedor."
      setError("Revendedores", message)
    } finally {
      disableLoad()
    }
  }

  const handleUpdateResellerStatus = async (resellerId: number, status: EEnableStatusType, onSuccess: () => void) => {
    enableLoad()

    try {
      const response = await PutRequest(
        API_ROUTES.ADMIN_RESELLER.UPDATE_STATUS(resellerId),
        { status },
        ERequestType.Admin,
      )

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess()
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro ao atualizar o status do revendedor."
      setError("Revendedores", message)
    } finally {
      disableLoad()
    }
  }

  const handleAssignCommissionPlan = async (
    resellerId: number,
    resellerCommissionPlanEntityId: number,
    onSuccess: () => void,
  ) => {
    enableLoad()

    try {
      const response = await PutRequest(
        API_ROUTES.ADMIN_RESELLER.ASSIGN_COMMISSION_PLAN(resellerId),
        { resellerCommissionPlanEntityId },
        ERequestType.Admin,
      )

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess()
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro ao atribuir o plano de comissão."
      setError("Revendedores", message)
    } finally {
      disableLoad()
    }
  }

  const handleGetAllCommissionPlans = async (onSuccess: (data: ResellerCommissionPlan[]) => void) => {
    enableLoad()

    try {
      const response = await GetRequest<ResellerCommissionPlan[]>(
        API_ROUTES.ADMIN_RESELLER.GET_ALL_COMMISSION_PLANS,
        ERequestType.Admin,
      )

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess((response.data ?? []).map((item) => new ResellerCommissionPlan(item)))
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro ao carregar os planos de comissão."
      setError("Planos de comissão", message)
    } finally {
      disableLoad()
    }
  }

  const handleCreateCommissionPlan = async (request: SaveResellerCommissionPlanRequest, onSuccess: () => void) => {
    enableLoad()

    try {
      const response = await PostRequest(
        API_ROUTES.ADMIN_RESELLER.CREATE_COMMISSION_PLAN,
        request,
        ERequestType.Admin,
      )

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess()
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro ao criar o plano de comissão."
      setError("Planos de comissão", message)
    } finally {
      disableLoad()
    }
  }

  const handleUpdateCommissionPlan = async (
    planId: number,
    request: SaveResellerCommissionPlanRequest,
    onSuccess: () => void,
  ) => {
    enableLoad()

    try {
      const response = await PutRequest(
        API_ROUTES.ADMIN_RESELLER.UPDATE_COMMISSION_PLAN(planId),
        request,
        ERequestType.Admin,
      )

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess()
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro ao atualizar o plano de comissão."
      setError("Planos de comissão", message)
    } finally {
      disableLoad()
    }
  }

  const handleMarkCommissionEntryAsPaid = async (
    commissionEntryId: number,
    reference: string | undefined,
    onSuccess: () => void,
  ) => {
    enableLoad()

    try {
      const response = await PostRequest(
        API_ROUTES.ADMIN_RESELLER.MARK_COMMISSION_ENTRY_PAID(commissionEntryId),
        { reference },
        ERequestType.Admin,
      )

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess()
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro ao marcar a comissão como paga."
      setError("Revendedores", message)
    } finally {
      disableLoad()
    }
  }

  const handleCreateCommissionPayment = async (
    resellerId: number,
    reference: string | undefined,
    onSuccess: () => void,
  ) => {
    enableLoad()

    try {
      const response = await PostRequest(
        API_ROUTES.ADMIN_RESELLER.CREATE_COMMISSION_PAYMENT(resellerId),
        { reference },
        ERequestType.Admin,
      )

      if (!response.success) {
        throw new Error(response.message)
      }

      onSuccess()
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erro ao criar o pagamento de comissão."
      setError("Revendedores", message)
    } finally {
      disableLoad()
    }
  }

  return {
    handleGetAllResellers,
    handleGetResellerDetail,
    handleUpdateResellerStatus,
    handleAssignCommissionPlan,
    handleGetAllCommissionPlans,
    handleCreateCommissionPlan,
    handleUpdateCommissionPlan,
    handleMarkCommissionEntryAsPaid,
    handleCreateCommissionPayment,
  }
}
